import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { ControlledPhoneField } from '@/common/components/Fields/ControlledPhoneField/ControlledPhoneField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import { Modal } from '@/common/components/Modal/Modal';
import { Stack } from '@/common/components/Stack/Stack';
import { MARKET } from '@/common/constants/markets';
import { useUpdateEmployeeDetails } from '@/common/integrations/menhir/employee/employee/employee.hooks';
import { IEmployee } from '@/common/integrations/menhir/employee/employee/employee.types';
import { parsePhone, phoneToString } from '@/common/utils/phone';
import { notify } from '@/common/utils/toast/toast';
import { useMarket } from '@/web/hooks/useMarket';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TContactsFormSchema,
  contactsFormSchema,
} from './ContactsFormModal.schema';

interface IContactsFormModalProps {
  employee: IEmployee;
  onClose: () => void;
}

export const ContactsFormModal = ({
  employee,
  onClose,
}: IContactsFormModalProps) => {
  const { t } = useTranslation();
  const { slug } = useMarket();
  const { mutateAsync } = useUpdateEmployeeDetails();

  const parsedPhoneObject = parsePhone(employee.phone ?? '');
  const isChangeEmailAllowed =
    slug !== MARKET.IT && employee.invitation === 'left';

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: employee.email,
      phone: {
        dialCode: parsedPhoneObject?.dialCode ?? '',
        number: parsedPhoneObject?.number ?? '',
      },
    },
    resolver: zodResolver(contactsFormSchema),
  });

  const onSubmit = (close: () => void) => async (form: TContactsFormSchema) => {
    const { email, phone } = form;
    try {
      const updatedData: {
        phone: string;
        email?: string;
      } = {
        phone: phoneToString(phone),
      };

      if (isChangeEmailAllowed) {
        updatedData.email = email;
      }

      await mutateAsync({ employeeId: employee.id, payload: updatedData });
      notify({
        message: t('general.alert.success.changes'),
        type: 'success',
      });
      close();
    } catch (err: any) {
      notify({
        type: 'error',
        message: t('errors.api.generic'),
      });
    }
  };

  return (
    <Modal
      title={t('dashboard.personal_info.sections.contacts.title')}
      onClose={onClose}
      footer={({ close }) => (
        <Stack horizontal gap="md">
          <Button
            variant="secondary"
            onPress={close}
            disabled={methods.formState.isSubmitting}
          >
            {t('dashboard.personal_info.cancel_button')}
          </Button>
          <Button
            type="submit"
            className="flex-1"
            loading={methods.formState.isSubmitting}
            onPress={methods.handleSubmit(onSubmit(close))}
          >
            {t('dashboard.personal_info.save_button')}
          </Button>
        </Stack>
      )}
    >
      {({ close }) => (
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit(close))}>
            <Stack gap="md">
              <ControlledTextField
                name="email"
                type="email"
                label={'email'}
                disabled={!isChangeEmailAllowed}
              />
              <ControlledPhoneField
                name="phone"
                labels={{
                  codeField: t('form.fields.dial_code'),
                  numberField: t('form.fields.phone_number'),
                }}
              />
            </Stack>
          </Form>
        </FormProvider>
      )}
    </Modal>
  );
};
